import { POOL, isMobile } from '@/config';
import { state, viewCams } from '@/store/state';
import type { Tile } from './Tile';

/** Concurrent-decoder cap; "unlimited" lifts it (every visible tile decodes). */
function capFor(): number {
  if (state.view.unlimited) return Infinity;
  return isMobile() ? POOL.maxMobile : POOL.maxDesktop;
}

/**
 * LRU decoder pool. Caps how many tiles hold a live hls.js/MSE decoder at once
 * (well under the browser WebMediaPlayer/GPU limits). Tiles beyond the cap show
 * an idle poster; the most-recently-visible win. Attaches are staggered. Only
 * tiles in the current view (see `viewCams`) are candidates.
 */
export const pool = {
  all: new Set<Tile>(),
  attached: new Set<Tile>(),
  queue: [] as Tile[],
  max: capFor(),
  draining: false,
  paused: false, // true while spotlight owns the screen
  timer: undefined as number | undefined,

  register(t: Tile): void { this.all.add(t); },
  unregister(t: Tile): void {
    this.all.delete(t);
    this.attached.delete(t);
    const i = this.queue.indexOf(t);
    if (i >= 0) this.queue.splice(i, 1);
  },

  refreshMax(): void {
    const m = capFor();
    if (m !== this.max) { this.max = m; this.reconcile(); }
  },

  reconcile(): void {
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.run(), 70);
  },

  run(): void {
    if (this.paused) return;
    const shown = new Set(viewCams());
    const want = [...this.all].filter((t) => t.visible && shown.has(t.cam.id));
    want.sort((a, b) => b.lastVisible - a.lastVisible);
    const keep = new Set(want.slice(0, this.max));
    for (const t of [...this.attached]) if (!keep.has(t)) t.detach(true);
    for (const t of want) if (!keep.has(t) && !t.attached) t.setStatus('idle');
    keep.forEach((t) => { if (!t.attached && !this.queue.includes(t)) this.queue.push(t); });
    this.drain();
  },

  drain(): void {
    if (this.draining) return;
    this.draining = true;
    const step = (): void => {
      const t = this.queue.shift();
      if (!t) { this.draining = false; return; }
      if (t.visible && !t.attached && !this.paused && viewCams().includes(t.cam.id)) void t.attach();
      window.setTimeout(step, 120);
    };
    step();
  },
};
